import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  Bell,
  AlertTriangle,
  CheckCircle2,
  X,
  ExternalLink,
  Loader2,
  RefreshCw,
  FileText
} from 'lucide-react';

const AlertsPanel = ({ onClose }) => {
  const { api } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchAlerts();
    fetchSummary();
  }, []);

  const fetchAlerts = async () => {
    try {
      const response = await api.get('/alerts');
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await api.get('/alerts/summary');
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const generateAlerts = async () => {
    setGenerating(true);
    try {
      const response = await api.post('/alerts/generate');
      toast.success(response.data.message);
      fetchAlerts();
      fetchSummary();
    } catch (error) {
      toast.error('Error al generar alertas');
    } finally {
      setGenerating(false);
    }
  };

  const markAsRead = async (alertId) => {
    try {
      await api.put(`/alerts/${alertId}/read`);
      setAlerts(alerts.map(a => a.id === alertId ? { ...a, read: true } : a));
      fetchSummary();
    } catch (error) {
      console.error('Error marking alert as read:', error);
    }
  };

  const dismissAlert = async (alertId) => {
    try {
      await api.delete(`/alerts/${alertId}`);
      setAlerts(alerts.filter(a => a.id !== alertId));
      fetchSummary();
      toast.success('Alerta descartada');
    } catch (error) {
      console.error('Error dismissing alert:', error);
    }
  };

  const getSeverityStyles = (severity) => {
    switch (severity) {
      case 'high':
        return 'border-rose-500/50 bg-rose-500/10';
      case 'medium':
        return 'border-amber-500/50 bg-amber-500/10';
      default:
        return 'border-slate-500/50 bg-slate-500/10';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'high':
        return <AlertTriangle className="w-5 h-5 text-rose-400" />;
      case 'medium':
        return <Bell className="w-5 h-5 text-amber-400" />;
      default:
        return <Bell className="w-5 h-5 text-slate-400" />;
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 z-50 flex justify-end"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="alerts-panel"
    >
      <div className="w-full max-w-md bg-card border-l border-border h-full overflow-hidden flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center space-x-3">
            <Bell className="w-5 h-5 text-blue-400" />
            <h2 className="font-semibold font-['Outfit']">Alertas Legales</h2>
            {summary?.unread_count > 0 && (
              <span className="px-2 py-0.5 text-xs bg-rose-500 text-white rounded-full">
                {summary.unread_count}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={generateAlerts}
              disabled={generating}
              data-testid="generate-alerts-btn"
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              title="Buscar nuevas alertas"
            >
              {generating ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <RefreshCw className="w-5 h-5" />
              )}
            </button>
            <button
              onClick={onClose}
              data-testid="close-alerts-btn"
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Summary */}
        {summary && (
          <div className="p-4 bg-slate-900/50 border-b border-border">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-white">{summary.total_alerts}</div>
                <div className="text-xs text-slate-400">Total</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-amber-400">{summary.unread_count}</div>
                <div className="text-xs text-slate-400">Sin leer</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-rose-400">{summary.high_priority_count}</div>
                <div className="text-xs text-slate-400">Urgentes</div>
              </div>
            </div>
          </div>
        )}

        {/* Alerts List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : alerts.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400 mb-4" />
              <h3 className="font-medium mb-2">¡Todo al día!</h3>
              <p className="text-sm text-slate-400 mb-4">No tienes alertas pendientes</p>
              <button
                onClick={generateAlerts}
                disabled={generating}
                className="text-sm text-blue-400 hover:text-blue-300"
              >
                Buscar nuevas alertas
              </button>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {alerts.map((alert) => (
                <div
                  key={alert.id}
                  className={`p-4 ${!alert.read ? 'bg-slate-900/30' : ''}`}
                  data-testid={`alert-item-${alert.id}`}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`p-2 rounded-lg ${getSeverityStyles(alert.severity)}`}>
                      {getSeverityIcon(alert.severity)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <h4 className={`font-medium text-sm ${!alert.read ? 'text-white' : 'text-slate-300'}`}>
                          {alert.title}
                        </h4>
                        <button
                          onClick={() => dismissAlert(alert.id)}
                          className="text-slate-500 hover:text-slate-300 ml-2"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-xs text-slate-400 mt-1 line-clamp-2">
                        {alert.message}
                      </p>
                      <div className="flex items-center space-x-3 mt-2">
                        {alert.action_url && (
                          <Link
                            to={alert.action_url}
                            onClick={() => markAsRead(alert.id)}
                            className="text-xs text-blue-400 hover:text-blue-300 flex items-center space-x-1"
                          >
                            <FileText className="w-3 h-3" />
                            <span>{['judge_ruling', 'hearing_reminder', 'win_probability_drop', 'reform_alert'].includes(alert.type) ? 'Ver caso' : alert.type === 'deadline_reminder' ? 'Ver plazo' : ['bill_progress', 'legislative'].includes(alert.type) ? 'Ver proyecto' : 'Ver contrato'}</span>
                          </Link>
                        )}
                        {alert.metadata?.effective_date && (
                          <span className="text-xs text-slate-500">
                            Vigencia: {alert.metadata.effective_date}
                          </span>
                        )}
                      </div>
                      {alert.metadata?.action_required && (
                        <div className="mt-2 px-2 py-1 bg-rose-500/10 border border-rose-500/20 rounded text-xs text-rose-400">
                          Acción requerida
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border bg-slate-900/50">
          <Link
            to="/legal-feed"
            onClick={onClose}
            className="flex items-center justify-center space-x-2 text-sm text-blue-400 hover:text-blue-300"
          >
            <span>Ver todas las actualizaciones legales</span>
            <ExternalLink className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AlertsPanel;
